import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Truck, Check, RefreshCw, Trash2, CloudUpload } from "lucide-react";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, FilterCard, tableDensity } from "@/components/ops-ui";

export default function FreightInvoices() {
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState("");
  const [monthOpts, setMonthOpts] = useState([]);
  const [bcs, setBcs] = useState([]);
  const [unbilled, setUnbilled] = useState([]);
  const [whCodes, setWhCodes] = useState([]);
  const [source, setSource] = useState("warehouse"); // "warehouse" | "shipments"
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ billing_company_id: "", freight_invoice_no: "", invoice_date: new Date().toISOString().slice(0,10), verified_amount_rm: "", shipment_ids: [], warehouse_codes: [], notes: "" });
  const [verify, setVerify] = useState(null);
  const { density, setDensity, filtersOpen, setFiltersOpen } = useUiPrefs("freight-invoices");

  const load = () => api.get(`/freight-invoices${month ? `?month=${month}` : ""}`).then(r => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  useEffect(() => {
    api.get("/billing-companies").then(r=>setBcs(r.data.filter(b=>b.is_supplier_side)));
    api.get("/freight-invoices").then(r => {
      setMonthOpts(Array.from(new Set(r.data.map(x => (x.invoice_date || "").slice(0,7)).filter(Boolean))).sort().reverse());
    });
  }, []);
  const fmtMonth = (ym) => { const [y, m] = ym.split("-"); return new Date(Number(y), Number(m)-1).toLocaleString("en-US", { month: "short", year: "numeric" }); };

  const loadUnbilled = async (bcId) => {
    if (!bcId) { setUnbilled([]); setWhCodes([]); return; }
    const [a, b] = await Promise.all([
      api.get(`/freight-invoices/unbilled/${bcId}`),
      api.get(`/freight-invoices/unbilled-warehouse-codes/${bcId}`),
    ]);
    setUnbilled(a.data); setWhCodes(b.data);
  };
  const onBcChange = (v) => { setForm({...form, billing_company_id: v, shipment_ids: [], warehouse_codes: []}); loadUnbilled(v); };

  const toggleShip = (sid) => {
    const set = new Set(form.shipment_ids);
    if (set.has(sid)) set.delete(sid); else set.add(sid);
    setForm({...form, shipment_ids: Array.from(set)});
  };
  const toggleCode = (code) => {
    const set = new Set(form.warehouse_codes);
    if (set.has(code)) set.delete(code); else set.add(code);
    setForm({...form, warehouse_codes: Array.from(set)});
  };

  const totalComputed = source === "warehouse"
    ? whCodes.filter(g => form.warehouse_codes.includes(g.warehouse_code)).reduce((acc, g) => acc + (g.estimated_freight_rm || 0), 0)
    : unbilled.filter(s => form.shipment_ids.includes(s.id)).reduce((acc, s) => acc + (s.estimated_freight_rm || s.freight_fee_rm || 0), 0);

  const create = async () => {
    if (!form.billing_company_id) return toast.error("Pick a billing company");
    if (source === "warehouse" && !form.warehouse_codes.length) return toast.error("Pick at least one warehouse code");
    if (source === "shipments" && !form.shipment_ids.length) return toast.error("Pick at least one shipment");
    try {
      const payload = {...form,
        shipment_ids: source === "shipments" ? form.shipment_ids : [],
        warehouse_codes: source === "warehouse" ? form.warehouse_codes : [],
        verified_amount_rm: form.verified_amount_rm ? parseFloat(form.verified_amount_rm) : null};
      await api.post("/freight-invoices", payload);
      toast.success("Freight invoice created");
      setOpen(false); setForm({ billing_company_id: "", freight_invoice_no: "", invoice_date: new Date().toISOString().slice(0,10), verified_amount_rm: "", shipment_ids: [], warehouse_codes: [], notes: "" });
      setUnbilled([]); setWhCodes([]); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveVerify = async () => {
    try {
      const payload = {
        freight_invoice_no: verify.freight_invoice_no || "",
        billing_company_id: verify.billing_company_id,
        shipment_ids: verify.shipment_ids || [],
        warehouse_codes: verify.warehouse_codes || [],
        verified_amount_rm: verify.verified_amount_rm ? parseFloat(verify.verified_amount_rm) : null,
        invoice_date: verify.invoice_date || null,
        notes: verify.notes || "",
        bukku_synced: !!verify.bukku_synced,
        bukku_ref: verify.bukku_ref || "",
      };
      await api.put(`/freight-invoices/${verify.id}`, payload);
      toast.success("Saved"); setVerify(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this freight invoice?")) return;
    await api.delete(`/freight-invoices/${id}`); load();
  };

  const syncBukku = async (id) => {
    const t = toast.loading("Syncing to Bukku…");
    try {
      const { data } = await api.post(`/bukku/sync/freight/${id}`);
      toast.success(`Bukku sync OK · ref ${data.ref}`, { id: t });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bukku sync failed", { id: t }); }
  };

  return (
    <div>
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="caption">Accounting sync</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Freight Invoices</h1>
          <p className="text-slate-500 mt-1 text-sm">Combine warehouse tracking codes billed by the same freight courier. Verify the final amount when the courier's invoice arrives.</p>
        </div>
        <div className="flex items-center gap-2">
          <DensityToggle density={density} setDensity={setDensity} />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="fi-new-button" className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-1.5" />New Freight Invoice</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>New freight invoice</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Billing company (courier) *</Label>
                  <Select value={form.billing_company_id} onValueChange={onBcChange}>
                    <SelectTrigger data-testid="fi-bc-select"><SelectValue placeholder="Pick a courier…" /></SelectTrigger>
                    <SelectContent>
                      {bcs.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Invoice date</Label><Input type="date" value={form.invoice_date} onChange={e=>setForm({...form, invoice_date:e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Freight invoice no. (fill later if needed)</Label><Input value={form.freight_invoice_no} data-testid="fi-no-input"
                  onChange={e=>setForm({...form, freight_invoice_no:e.target.value})} placeholder="Pending" /></div>
                <div><Label>Verified amount RM (optional)</Label><Input type="number" step="0.01" data-testid="fi-verified-input"
                  value={form.verified_amount_rm} onChange={e=>setForm({...form, verified_amount_rm:e.target.value})} placeholder={`computed RM ${totalComputed.toFixed(2)}`} /></div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="caption">Bill by</span>
                  <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
                    <button type="button" data-testid="fi-source-warehouse" onClick={()=>setSource("warehouse")}
                      className={`px-3 py-1.5 ${source==="warehouse"?"bg-slate-900 text-white":"bg-white text-slate-600"}`}>Arrived warehouse codes</button>
                    <button type="button" data-testid="fi-source-shipments" onClick={()=>setSource("shipments")}
                      className={`px-3 py-1.5 ${source==="shipments"?"bg-slate-900 text-white":"bg-white text-slate-600"}`}>China shipments</button>
                  </div>
                </div>

                {source === "warehouse" ? (
                  <>
                    <Label>Arrived &amp; checked warehouse codes for this courier</Label>
                    <div className="border border-slate-200 rounded max-h-64 overflow-auto mt-1">
                      <table className="excel-table w-full text-xs">
                        <thead><tr><th></th><th>Warehouse Code</th><th>Checked</th><th>Freight</th><th className="text-right">CBM</th><th className="text-right">Est. RM</th></tr></thead>
                        <tbody>
                          {whCodes.map(g => (
                            <tr key={g.warehouse_code}>
                              <td><Checkbox data-testid={`fi-pick-wh-${g.warehouse_code}`} checked={form.warehouse_codes.includes(g.warehouse_code)} onCheckedChange={()=>toggleCode(g.warehouse_code)} /></td>
                              <td className="font-mono font-semibold">{g.warehouse_code}</td>
                              <td className="font-mono">{(g.checked_at||"").slice(0,10) || "—"}</td>
                              <td className="font-mono">{g.freight_company_code}</td>
                              <td className="text-right font-mono">{g.total_cbm}</td>
                              <td className="text-right rm-badge">RM {(g.estimated_freight_rm ?? 0).toFixed(2)}</td>
                            </tr>
                          ))}
                          {whCodes.length === 0 && form.billing_company_id && <tr><td colSpan={6} className="text-center py-4 text-slate-400">No unbilled arrived codes for this courier</td></tr>}
                          {!form.billing_company_id && <tr><td colSpan={6} className="text-center py-4 text-slate-400">Pick a courier first</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <>
                    <Label>Unbilled shipments for this courier</Label>
                    <div className="border border-slate-200 rounded max-h-64 overflow-auto mt-1">
                      <table className="excel-table w-full text-xs">
                        <thead><tr><th></th><th>Tracking</th><th>Ship date</th><th>Rate</th><th className="text-right">CBM</th><th className="text-right">Est. RM</th></tr></thead>
                        <tbody>
                          {unbilled.map(s => (
                            <tr key={s.id}>
                              <td><Checkbox data-testid={`fi-pick-${s.tracking_code}`} checked={form.shipment_ids.includes(s.id)} onCheckedChange={()=>toggleShip(s.id)} /></td>
                              <td className="font-mono">{s.tracking_code}</td>
                              <td className="font-mono">{s.shipment_date || "—"}</td>
                              <td className="font-mono">{s.shipping_rate_code}</td>
                              <td className="text-right font-mono">{s.total_actual_cbm || s.total_pre_cbm}</td>
                              <td className="text-right rm-badge">RM {s.estimated_freight_rm ?? "—"}</td>
                            </tr>
                          ))}
                          {unbilled.length === 0 && form.billing_company_id && <tr><td colSpan={6} className="text-center py-4 text-slate-400">No unbilled shipments</td></tr>}
                          {!form.billing_company_id && <tr><td colSpan={6} className="text-center py-4 text-slate-400">Pick a courier first</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {(form.warehouse_codes.length > 0 || form.shipment_ids.length > 0) && (
                  <div className="mt-2 text-sm text-right">
                    <span className="caption mr-2">Auto freight (editable later)</span>
                    <span className="rm-badge font-bold text-lg">RM {totalComputed.toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={create} data-testid="fi-save-button" className="bg-slate-900">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <div className="mb-4">
        <FilterCard open={filtersOpen} setOpen={setFiltersOpen} activeCount={month ? 1 : 0} testid="fi-filters">
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Month</label>
              <Select value={month || "all"} onValueChange={v => setMonth(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="fi-month-filter"><SelectValue placeholder="All months" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="fi-month-all">All months</SelectItem>
                  {monthOpts.map(ym => (
                    <SelectItem key={ym} value={ym} data-testid={`fi-month-${ym}`}>{fmtMonth(ym)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </FilterCard>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`excel-table w-full ${tableDensity(density)}`} data-testid="fi-table">
          <thead><tr>
            <th>Invoice No</th><th>Billing Company</th><th>Date</th><th>Shipments</th>
            <th className="text-right">Computed RM</th><th className="text-right">Verified RM</th>
            <th className="text-right">Round Adj.</th><th>Status</th><th>Bukku</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`fi-row-${r.id}`}>
                <td className="font-mono font-semibold">{r.freight_invoice_no || <span className="text-amber-600">— pending —</span>}</td>
                <td>{r.billing_company?.name}</td>
                <td className="font-mono text-xs">{r.invoice_date || "—"}</td>
                <td className="text-xs">{r.shipments?.length ? r.shipments.map(s=>s.tracking_code).join(", ") : <span className="font-mono">{(r.warehouse_codes||[]).join(", ")}</span>}</td>
                <td className="text-right rm-badge">RM {r.computed_amount_rm}</td>
                <td className="text-right rm-badge font-bold">{r.verified_amount_rm != null ? `RM ${r.verified_amount_rm}` : "—"}</td>
                <td className="text-right rm-badge">{r.rounding_adjustment_rm != null ? `RM ${r.rounding_adjustment_rm}` : "—"}</td>
                <td><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === "verified" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>{r.status}</span></td>
                <td>{r.bukku_synced ? <span className="text-xs font-mono text-emerald-600 flex items-center gap-1"><Check className="w-3 h-3" />{r.bukku_ref || "synced"}</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                <td className="text-right whitespace-nowrap">
                  {!r.bukku_synced && r.status === "verified" && (
                    <Button variant="ghost" size="sm" onClick={()=>syncBukku(r.id)} data-testid={`fi-sync-${r.id}`} title="Sync to Bukku">
                      <CloudUpload className="w-4 h-4 text-sky-600" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={()=>setVerify(r)} data-testid={`fi-verify-${r.id}`}><RefreshCw className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={()=>del(r.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><td colSpan={10} className="text-center py-12 text-slate-400"><Truck className="w-8 h-8 mx-auto mb-2" />No freight invoices yet.</td></tr>)}
          </tbody>
        </table>
      </div>

      <Dialog open={!!verify} onOpenChange={(o)=>!o && setVerify(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Verify freight invoice</DialogTitle></DialogHeader>
          {verify && (
            <div className="space-y-3">
              <div className="p-3 rounded bg-slate-50 text-sm">
                <div><span className="caption mr-2">Courier</span>{verify.billing_company?.name}</div>
                <div><span className="caption mr-2">{verify.shipments?.length ? "Shipments" : "Warehouse codes"}</span>{verify.shipments?.length ? verify.shipments.map(s=>s.tracking_code).join(", ") : (verify.warehouse_codes||[]).join(", ")}</div>
                <div><span className="caption mr-2">Computed</span><span className="rm-badge font-bold">RM {verify.computed_amount_rm}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Freight invoice no.</Label><Input data-testid="fi-verify-no" value={verify.freight_invoice_no || ""}
                  onChange={e=>setVerify({...verify, freight_invoice_no:e.target.value})} /></div>
                <div><Label>Invoice date</Label><Input type="date" value={verify.invoice_date || ""}
                  onChange={e=>setVerify({...verify, invoice_date:e.target.value})} /></div>
              </div>
              <div><Label>Verified final amount RM *</Label><Input type="number" step="0.01" data-testid="fi-verify-amount"
                value={verify.verified_amount_rm ?? ""} onChange={e=>setVerify({...verify, verified_amount_rm:e.target.value})} /></div>
              {verify.verified_amount_rm != null && verify.verified_amount_rm !== "" && (
                <div className="text-sm text-slate-500">Rounding adjustment: <span className="rm-badge font-bold">RM {(parseFloat(verify.verified_amount_rm) - (verify.computed_amount_rm || 0)).toFixed(2)}</span></div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!verify.bukku_synced} onCheckedChange={v=>setVerify({...verify, bukku_synced: !!v})} />
                Mark as synced to Bukku
              </label>
              {verify.bukku_synced && (
                <div><Label>Bukku reference</Label><Input value={verify.bukku_ref || ""}
                  onChange={e=>setVerify({...verify, bukku_ref:e.target.value})} placeholder="Bukku doc ID" /></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setVerify(null)}>Cancel</Button>
            <Button onClick={saveVerify} data-testid="fi-verify-save" className="bg-slate-900">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
